import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Search, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import axios from "axios";
import { EDGAR_PROFILE_URL } from "../data/filings";
import { COMPANY, DISCLAIMER } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMS = ["All", "8-K", "10-Q", "10-K", "SC 14F1", "SCHEDULE 13D", "3"];

function formatSyncTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { timeZone: "UTC", hour12: false }) + " UTC";
  } catch {
    return iso;
  }
}

export default function SecFilings() {
  const [q, setQ] = useState("");
  const [form, setForm] = useState("All");
  const [filings, setFilings] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [f, s] = await Promise.all([
        axios.get(`${API}/filings`),
        axios.get(`${API}/filings/status`),
      ]);
      setFilings(Array.isArray(f.data) ? f.data : []);
      setStatus(s.data || null);
    } catch (err) {
      setLoadError("Unable to load filings from the server. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/filings/sync`);
    } catch {
      // swallow — status endpoint will show the failure state.
    } finally {
      await loadAll();
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    return filings.filter((f) => {
      const okForm = form === "All" || (f.form || "").toUpperCase() === form.toUpperCase();
      const hay = `${f.form} ${f.filed} ${f.description}`.toLowerCase();
      const okQ = !q || hay.includes(q.toLowerCase());
      return okForm && okQ;
    });
  }, [q, form, filings]);

  const syncOk = status?.last_sync_status === "ok";
  const syncFailed = status?.last_sync_status === "failed";

  return (
    <div data-testid="page-sec-filings">
      <section className="bg-[#14110E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> Official Company Filings
          </div>
          <h1 className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            SEC EDGAR Filings
          </h1>
          <p className="mt-6 text-[#F5F2EB] leading-relaxed max-w-3xl">
            Complete list of statutory disclosures filed with the U.S. Securities and Exchange
            Commission (SEC). Every filing link resolves to the official SEC EDGAR database at
            sec.gov. The mirror below refreshes automatically once per day from
            data.sec.gov.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs uppercase tracking-[0.18em] text-[#A79E93]">
            <span className="text-white break-all">{COMPANY.market}: {COMPANY.ticker}</span>
            <span aria-hidden>·</span>
            <span className="break-all">CIK {COMPANY.cik}</span>
            <span aria-hidden>·</span>
            <span>FYE {COMPANY.fiscalYearEnd}</span>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={EDGAR_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 btn-gradient rounded-full px-6 py-3.5 text-sm font-semibold"
              data-testid="filings-edgar-primary"
            >
              Open SEC EDGAR profile <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {/* Sync status bar */}
        <div
          className="mb-8 border border-[#DED6C6] bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
          data-testid="filings-sync-bar"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {syncFailed ? (
              <AlertTriangle className="h-5 w-5 text-[#B91C1C] flex-shrink-0 mt-0.5" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-[#14110E] flex-shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <div className="eyebrow text-[#A79E93]">EDGAR Sync</div>
              <div className="mt-1 text-sm text-[#14110E] font-semibold" data-testid="filings-sync-status">
                {syncOk && `Up to date · last synced ${formatSyncTime(status?.last_sync)}`}
                {syncFailed && (
                  <>
                    Last sync failed —{" "}
                    <span className="text-[#B91C1C] font-normal">{status?.last_sync_message}</span>
                    {status?.last_sync && (
                      <span className="text-[#4A3D30] font-normal">
                        {" "}Last good data from {formatSyncTime(status.last_sync)}.
                      </span>
                    )}
                  </>
                )}
                {!syncOk && !syncFailed && "Waiting for first sync…"}
              </div>
              <div className="mt-1 font-mono text-[11px] text-[#A79E93]">
                Source data.sec.gov · CIK {COMPANY.cik} · {status?.total_stored ?? filings.length} filings in mirror
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={triggerSync}
            disabled={syncing || loading}
            className="inline-flex items-center justify-center gap-2 border border-[#14110E] text-[#14110E] hover:bg-[#14110E] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed px-5 py-2.5 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
            data-testid="filings-sync-now"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
          <div className="lg:col-span-5 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A79E93]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search filings (form, date, description)"
              className="w-full pl-11 pr-4 py-3.5 border border-[#DED6C6] focus:outline-none focus:border-[#14110E] text-sm bg-white"
              data-testid="filings-search"
            />
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-2">
            {FORMS.map((frm) => (
              <button
                key={frm}
                onClick={() => setForm(frm)}
                data-testid={`filings-filter-${frm.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] border transition-colors ${
                  form === frm
                    ? "bg-[#14110E] border-[#14110E] text-white"
                    : "border-[#DED6C6] text-[#4A3D30] hover:border-[#14110E] hover:text-[#14110E]"
                }`}
              >
                {frm}
              </button>
            ))}
          </div>
        </div>

        {/* Filings list — table on md+, card layout on mobile (no clipped content at 375px) */}
        <div className="mt-10" data-testid="filings-table">
          {/* Mobile: card layout */}
          <div className="md:hidden space-y-3">
            {loading && (
              <div className="border border-[#DED6C6] px-5 py-10 text-center text-sm text-[#A79E93]" data-testid="filings-loading">
                Loading filings…
              </div>
            )}
            {!loading && loadError && (
              <div className="border border-[#FCA5A5] bg-[#FEF2F2] px-5 py-8 text-center text-sm text-[#B91C1C]" data-testid="filings-load-error">
                {loadError}
              </div>
            )}
            {!loading && !loadError && filtered.length === 0 && (
              <div className="border border-[#DED6C6] px-5 py-10 text-center text-sm text-[#A79E93]" data-testid="filings-empty">
                No filings match your search.
              </div>
            )}
            {!loading && !loadError && filtered.map((f, i) => (
              <a
                key={f.accession || f.url || i}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block border border-[#DED6C6] hover:border-[#14110E] p-4 bg-white transition-colors"
                data-testid={`filing-card-${i}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[#14110E] font-semibold text-sm break-all">{f.form}</span>
                  <span className="font-mono text-[11px] text-[#4A3D30] whitespace-nowrap">{f.filed}</span>
                </div>
                <p className="mt-2 text-sm text-[#14110E] break-words leading-snug">{f.description}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[#F26A1B] group-hover:text-[#E63E1A] text-xs font-semibold">
                  View filing <ArrowUpRight className="h-3.5 w-3.5" />
                </div>
              </a>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block border border-[#DED6C6] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EA] text-[#4A3D30] font-mono text-[11px] uppercase tracking-[0.2em]">
                <tr>
                  <th className="text-left px-5 py-4 w-36">Form</th>
                  <th className="text-left px-5 py-4 w-40">Filed</th>
                  <th className="text-left px-5 py-4">Description</th>
                  <th className="text-right px-5 py-4 w-40">Filing</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="px-5 py-16 text-center text-[#A79E93]">
                      Loading filings…
                    </td>
                  </tr>
                )}
                {!loading && loadError && (
                  <tr>
                    <td colSpan={4} className="px-5 py-16 text-center text-[#B91C1C]">
                      {loadError}
                    </td>
                  </tr>
                )}
                {!loading && !loadError && filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-16 text-center text-[#A79E93]">
                      No filings match your search.
                    </td>
                  </tr>
                )}
                {!loading &&
                  !loadError &&
                  filtered.map((f, i) => (
                    <tr
                      key={f.accession || f.url || i}
                      className="border-t border-[#DED6C6] hover:bg-[#F4F1EA] transition-colors"
                    >
                      <td className="px-5 py-4 font-mono text-[#14110E] font-semibold">{f.form}</td>
                      <td className="px-5 py-4 font-mono text-[#4A3D30]">{f.filed}</td>
                      <td className="px-5 py-4 text-[#14110E]">{f.description}</td>
                      <td className="px-5 py-4 text-right">
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[#F26A1B] hover:text-[#E63E1A] font-semibold"
                          data-testid={`filing-link-${i}`}
                        >
                          View filing <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-8 text-xs text-[#8A8075] leading-relaxed max-w-4xl" data-testid="filings-disclaimer">
          {DISCLAIMER.forwardLooking} {DISCLAIMER.edgarNote}
        </p>
      </section>
    </div>
  );
}
